import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, TextField, MenuItem, Checkbox, Chip, IconButton } from "@mui/material";
import * as Yup from "yup";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { FiX, FiChevronDown } from "react-icons/fi";
import { Formik } from "formik";
import { useUpdateUserMutation, useGetUserDetailQuery } from "store/slices/apis/userApiSlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import { useGetUserRoleListQuery } from "store/slices/apis/userRoleApiSlice";
import { useParams } from "react-router-dom";

const EditUser = (props) => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const isProjectRoute = !!projectId;
    const { open, handleClose, rowDataId } = props;

    const { data: userDetail } = useGetUserDetailQuery(rowDataId);
    const rowData = userDetail?.data;

    const [updateUser, { isLoading }] = useUpdateUserMutation();
    const projectType = localStorage.getItem("projectType");

    const { data: projectDataList } = useGetAllProjectsQuery(projectType, {
        skip: isProjectRoute,
    });
    const projectData = projectDataList?.data || [];

    const { data: roleDataList } = useGetUserRoleListQuery({
        pageNumber: 1,
        pageSize: 50,
        projectId: projectId,
        includeSystemRoles: false,
    },
        {
            skip: !isProjectRoute,
        }
    );
    const roleData = roleDataList?.data || [];

    const initialValues = {
        email: rowData?.email || "",
        firstName: rowData?.firstName || "",
        lastName: rowData?.lastName || "",
        designation: rowData?.designation || "",
        phoneNumber: rowData?.phoneNumber || "",
        loginMethod: rowData?.loginMethod || "",
        oid: rowData?.oid || "",
        roleName: rowData?.roleName?.filter((id) => roleData?.some((p) => p?.displayName + p?.projectId === id)) || [],
        projects: rowData?.projects || [],
    };

    const validationSchema = (isProjectRoute) => Yup.object().shape({
        email: Yup.string()
            .email("Enter a valid email address")
            .required("Email field is required"),
        firstName: Yup.string().required("First Name field is required"),
        lastName: Yup.string().required("Last Name field is required"),
        designation: Yup.string().required("Designation field is required"),
        phoneNumber: Yup.string()
            .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
            .required("Mobile field is required"),
        loginMethod: Yup.string().required("Login method field is required"),

        oid: Yup.string().when("loginMethod", {
            is: (val) => val === "sso",
            then: () => Yup.string().required("Object Id is required"),
            otherwise: () => Yup.string().notRequired(),
        }),

        // optional when global user
        roleName: isProjectRoute
            ? Yup.array()
                .min(1, "Select at least one role")
                .required("Role field is required")
            : Yup.array().nullable(),

        // optional when project user
        projects: !isProjectRoute
            ? Yup.array()
                .min(1, "Select at least one project")
                .required("Project field is required")
            : Yup.array().nullable(),
    });

    const schema = useMemo(() => validationSchema(isProjectRoute), [isProjectRoute]);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        let reqBody = {
            id: rowData?.id,
            email: values?.email,
            firstName: values?.firstName,
            lastName: values?.lastName,
            designation: values?.designation,
            phoneNumber: values?.phoneNumber,
            isProjectUser: !!projectId,
            roleName: projectId ? values.roleName : ["Admin"],
            projects: values.projects,
            // projects: projectId ? [projectId, ...rowData?.projects] : values.projects,
            loginMethod: values?.loginMethod,
            oid: values?.loginMethod === 'sso' ? values?.oid : "",
            ...(projectId && { currentProject: projectId })
        };

        // console.log(reqBody);


        try {
            const response = await updateUser({ body: reqBody }).unwrap();
            openNotification("User updated successfully!", 'success')
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            openNotification(error?.data?.message || error?.error || "Something went wrong", "error");
            //handleClose();
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const labelTextStyle = {
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "100%",
        color: "#5F5F5F",
        fontFamily: "Outfit, sans-serif",
        mb: 1,
        mt: 3,
    };

    return (
        <>
            {/* ---------- Popup Dialog ---------- */}
            <Dialog
                open={open}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#ECECEC",
                        pb: 2,
                        pt: 3,
                        width: "100%",
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Edit User
                        </Typography>
                        <Typography sx={{
                            pt: 2, fontWeight: 400, fontSize: "14px", lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif",
                        }}>
                            Update global user
                        </Typography>
                    </Box>

                </DialogTitle>
                <DialogContent sx={{ width: "100%", p: 4, overflowX: "hidden", boxSizing: "border-box" }}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={schema}
                        enableReinitialize={true}
                        onSubmit={handleSubmit}
                    >
                        {({ values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldValue,
                            setFieldTouched,
                            setTouched, }) => (
                            <form onSubmit={handleSubmit}>
                                <Box sx={{ mt: 3 }}>
                                    <Typography sx={labelTextStyle}>
                                        Email
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Email"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.email && errors.email && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.email}
                                        </Typography>
                                    )}

                                    <Typography sx={labelTextStyle}>
                                        First Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="firstName"
                                        value={values.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter First Name"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.firstName && errors.firstName && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.firstName}
                                        </Typography>
                                    )}

                                    <Typography sx={labelTextStyle}>
                                        Last Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="lastName"
                                        value={values.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Last Name"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.lastName && errors.lastName && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.lastName}
                                        </Typography>
                                    )}

                                    <Typography sx={labelTextStyle}>
                                        Designation
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="designation"
                                        value={values.designation}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Designation"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.designation && errors.designation && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.designation}
                                        </Typography>
                                    )}

                                    <Typography sx={labelTextStyle}>
                                        Mobile
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="phoneNumber"
                                        value={values.phoneNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Mobile"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.phoneNumber && errors.phoneNumber && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.phoneNumber}
                                        </Typography>
                                    )}

                                    {projectId && (
                                        <>
                                            <Typography sx={labelTextStyle}>
                                                Role
                                            </Typography>
                                            <Box>
                                                {/* Select with checkbox dropdown */}
                                                <TextField
                                                    select
                                                    fullWidth
                                                    name="roleName"
                                                    value={values.roleName}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setFieldValue("roleName", typeof value === "string" ? value.split(",") : value);
                                                    }}
                                                    onBlur={handleBlur}
                                                    SelectProps={{
                                                        multiple: true,
                                                        displayEmpty: true,
                                                        IconComponent: FiChevronDown,
                                                        renderValue: () => <span>Select Roles</span>,
                                                        MenuProps: {
                                                            anchorOrigin: {
                                                                vertical: "bottom",
                                                                horizontal: "left",
                                                            },
                                                            transformOrigin: {
                                                                vertical: "bottom",
                                                                horizontal: "left",
                                                            },
                                                            PaperProps: {
                                                                style: { maxHeight: 300 },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root fieldset": { border: "none" },
                                                        "& .MuiSelect-icon": { color: "#292D32", fontSize: "18px", right: "25px" },
                                                        backgroundColor: "#E7E7EB",
                                                        borderRadius: "5px",
                                                    }}
                                                >
                                                    <MenuItem disabled value="">
                                                        Select Roles
                                                    </MenuItem>

                                                    {roleData?.map((proj) => (
                                                        <MenuItem key={proj?.id} value={proj?.displayName + '' + proj?.projectId}>
                                                            <Checkbox checked={values?.roleName?.includes(proj?.displayName + '' + proj?.projectId)} />
                                                            {proj?.displayName}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>

                                                {values.roleName.length > 0 && (
                                                    <Box
                                                        sx={{
                                                            mt: "3px",
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 1,
                                                            alignItems: "center",
                                                            p: 1,
                                                            border: "1px solid #ddd",
                                                            borderRadius: "5px",
                                                        }}
                                                    >
                                                        {values?.roleName
                                                            // 1. Filter: Only keep IDs that have a match in roleData
                                                            // .filter((id) => roleData?.some((p) => p?.displayName + p?.projectId === id))
                                                            // 2. Map: Render the Chips for valid matches
                                                            .map((id) => {
                                                                const proj = roleData?.find((p) => p?.displayName + p?.projectId === id);

                                                                return (
                                                                    <Chip
                                                                        key={id}
                                                                        label={proj?.displayName}
                                                                        onDelete={() =>
                                                                            setFieldValue(
                                                                                "roleName",
                                                                                values.roleName.filter((x) => x !== id)
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            background: "#F0F0F0",
                                                                            fontFamily: "Outfit",
                                                                            marginRight: "5px",
                                                                            marginBottom: "5px"
                                                                        }}
                                                                    />
                                                                );
                                                            })
                                                        }
                                                        {/* {values.roleName.map((id) => {
                                                            const proj = roleData.find((p) => p.id === id);
                                                            return (
                                                                <Chip
                                                                    key={id}
                                                                    label={proj?.displayName || id}
                                                                    onDelete={() =>
                                                                        setFieldValue(
                                                                            "roleName",
                                                                            values.roleName.filter((x) => x !== id)
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        background: "#F0F0F0",
                                                                    }}
                                                                />
                                                            );
                                                        })} */}

                                                        {values.roleName.length > 0 && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setFieldValue("roleName", [])}
                                                            >
                                                                <FiX />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                            {touched.roleName && errors.roleName && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.roleName}
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    {!projectId && (
                                        <>
                                            <Typography sx={labelTextStyle}>
                                                Project
                                            </Typography>
                                            <Box>
                                                {/* Select with checkbox dropdown */}
                                                <TextField
                                                    select
                                                    fullWidth
                                                    name="projects"
                                                    value={values.projects}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setFieldValue("projects", typeof value === "string" ? value.split(",") : value);
                                                    }}
                                                    onBlur={handleBlur}
                                                    SelectProps={{
                                                        multiple: true,
                                                        displayEmpty: true,
                                                        IconComponent: FiChevronDown,
                                                        renderValue: () => <span>Select Projects</span>,
                                                        MenuProps: {
                                                            anchorOrigin: {
                                                                vertical: "bottom",
                                                                horizontal: "left",
                                                            },
                                                            transformOrigin: {
                                                                vertical: "bottom",
                                                                horizontal: "left",
                                                            },
                                                            PaperProps: {
                                                                style: { maxHeight: 300 },
                                                            },
                                                        },
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root fieldset": { border: "none" },
                                                        "& .MuiSelect-icon": { color: "#292D32", fontSize: "18px", right: "25px" },
                                                        backgroundColor: "#E7E7EB",
                                                        borderRadius: "5px",
                                                    }}
                                                >
                                                    <MenuItem disabled value="">
                                                        Select Projects
                                                    </MenuItem>

                                                    {projectData?.map((proj) => (
                                                        <MenuItem key={proj.projectId} value={proj.projectId}>
                                                            <Checkbox checked={values.projects.includes(proj.projectId)} />
                                                            {proj.projectName}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>

                                                {values.projects.length > 0 && (
                                                    <Box
                                                        sx={{
                                                            mt: "3px",
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: 1,
                                                            alignItems: "center",
                                                            p: 1,
                                                            border: "1px solid #ddd",
                                                            borderRadius: "5px",
                                                        }}
                                                    >
                                                        {values.projects.map((id) => {
                                                            const proj = projectData.find((p) => p.projectId === id);
                                                            return (
                                                                <Chip
                                                                    key={id}
                                                                    label={proj?.projectName || id}
                                                                    onDelete={() =>
                                                                        setFieldValue(
                                                                            "projects",
                                                                            values.projects.filter((x) => x !== id)
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        background: "#F0F0F0",
                                                                    }}
                                                                />
                                                            );
                                                        })}

                                                        {values.projects.length > 0 && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setFieldValue("projects", [])}
                                                            >
                                                                <FiX />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                            {touched.projects && errors.projects && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.projects}
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    <Typography sx={labelTextStyle}>
                                        Login Method
                                    </Typography>
                                    <TextField select fullWidth
                                        name="loginMethod"
                                        value={values.loginMethod}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        SelectProps={{ displayEmpty: true, IconComponent: FiChevronDown }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    border: "none",
                                                },
                                                "& .MuiSelect-icon": {
                                                    color: "#292D32",
                                                    fontSize: "18px",
                                                    right: "25px",
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            sx: {
                                                backgroundColor: "#E7E7EB",
                                                //width: { xs: "100%", md: "50%" },
                                                width: "100%",
                                                borderRadius: "5px",
                                            },
                                        }}
                                    >
                                        <MenuItem value="" disabled>Select Login Method</MenuItem>
                                        <MenuItem value="sso">SSO</MenuItem>
                                        <MenuItem value="username">Username</MenuItem>
                                    </TextField>
                                    {touched.loginMethod && errors.loginMethod && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.loginMethod}
                                        </Typography>
                                    )}

                                    {values?.loginMethod === "sso" && (
                                        <>
                                            <Typography sx={labelTextStyle}>
                                                Object Id
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                name="oid"
                                                value={values.oid}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter Object Id"
                                                InputProps={{
                                                    sx: {
                                                        height: "50px",
                                                        borderRadius: "8px",
                                                        backgroundColor: "#fff",

                                                        "& input::placeholder": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                            opacity: 1,
                                                        },

                                                        "& input": {
                                                            fontWeight: 400,
                                                            fontSize: "16px",
                                                            lineHeight: "100%",
                                                            letterSpacing: "0.16px",
                                                            color: "#0A0A0C",
                                                            fontFamily: "Outfit, sans-serif",
                                                        },
                                                    },
                                                }}
                                            />
                                            {touched.oid && errors.oid && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                    {errors.oid}
                                                </Typography>
                                            )}
                                        </>
                                    )}

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: { xs: 2, md: 1 },
                                            width: "100%",
                                            boxSizing: "border-box",
                                            overflowX: "hidden",
                                            mt: 4,
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Button onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "18px",
                                                py: "12px",
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditUser;